import { useEffect, useState } from 'react';
import { clientFetch } from './api';
import ClientShell from './ClientShell';

const styles = {
  container: {
    padding: '24px',
  },
  createFormSection: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '32px',
  },
  formTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#FFF',
    marginBottom: '16px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#9CA3AF',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0B1120',
    border: '1px solid #1F2937',
    borderRadius: '6px',
    color: '#FFF',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0B1120',
    border: '1px solid #1F2937',
    borderRadius: '6px',
    color: '#FFF',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '100px',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0B1120',
    border: '1px solid #1F2937',
    borderRadius: '6px',
    color: '#FFF',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  submitButton: {
    backgroundColor: '#3B82F6',
    color: '#FFF',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
  },
  ticketsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  ticketItem: {
    backgroundColor: '#111827',
    border: '1px solid #1F2937',
    borderRadius: '8px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  ticketItemExpanded: {
    borderColor: '#3B82F6',
  },
  ticketHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  ticketNumber: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3B82F6',
  },
  ticketTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#FFF',
    flex: 1,
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  badgeOpen: {
    backgroundColor: '#3b82f6',
    color: '#fff',
  },
  badgeInProgress: {
    backgroundColor: '#f59e0b',
    color: '#1f2937',
  },
  badgeClosed: {
    backgroundColor: '#10b981',
    color: '#fff',
  },
  priorityHigh: {
    backgroundColor: '#ef4444',
    color: '#fff',
  },
  priorityMedium: {
    backgroundColor: '#f59e0b',
    color: '#1f2937',
  },
  priorityLow: {
    backgroundColor: '#6b7280',
    color: '#fff',
  },
  ticketDetails: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #1F2937',
  },
  ticketDetail: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginBottom: '8px',
  },
  messageThread: {
    marginTop: '16px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  message: {
    backgroundColor: '#0B1120',
    border: '1px solid #1F2937',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '8px',
    fontSize: '13px',
  },
  messageSender: {
    fontWeight: '600',
    color: '#3B82F6',
    marginBottom: '4px',
  },
  messageText: {
    color: '#FFF',
  },
  messageTime: {
    fontSize: '12px',
    color: '#6B7280',
    marginTop: '4px',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  errorText: {
    color: '#EF4444',
    fontSize: '14px',
  },
};

export default function ClientTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [ticketMessages, setTicketMessages] = useState({});
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
  });

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setLoading(true);
        const data = await clientFetch('/client/portal/tickets');
        setTickets(Array.isArray(data) ? data : data.tickets || []);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load tickets');
        console.error('Tickets error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, []);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.description) {
      setError('Subject and description are required');
      return;
    }

    try {
      setCreating(true);
      const newTicket = await clientFetch('/client/portal/tickets', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      setTickets([newTicket, ...tickets]);
      setFormData({ subject: '', description: '', category: 'general', priority: 'medium' });
      setError(null);
    } catch (err) {
      setError('Failed to create ticket');
      console.error('Create ticket error:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleExpandTicket = async (ticketId) => {
    if (expandedId === ticketId) {
      setExpandedId(null);
      return;
    }

    try {
      if (!ticketMessages[ticketId]) {
        const messages = await clientFetch(`/client/portal/tickets/${ticketId}`);
        setTicketMessages((prev) => ({
          ...prev,
          [ticketId]: messages.messages || [],
        }));
      }
      setExpandedId(ticketId);
    } catch (err) {
      console.error('Error loading ticket messages:', err);
      setError('Failed to load ticket details');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span style={{ ...styles.badge, ...styles.badgeOpen }}>Open</span>;
      case 'in_progress':
        return <span style={{ ...styles.badge, ...styles.badgeInProgress }}>In Progress</span>;
      case 'closed':
        return <span style={{ ...styles.badge, ...styles.badgeClosed }}>Closed</span>;
      default:
        return <span style={{ ...styles.badge }}>{status}</span>;
    }
  };

  const getPriorityBadge = (priority) => {
    const style = priority === 'high' ? styles.priorityHigh : priority === 'medium' ? styles.priorityMedium : styles.priorityLow;
    return <span style={{ ...styles.badge, ...style }}>{priority.charAt(0).toUpperCase() + priority.slice(1)}</span>;
  };

  if (loading) {
    return (
      <ClientShell title="Support Tickets">
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading tickets...</p>
        </div>
      </ClientShell>
    );
  }

  return (
    <ClientShell title="Support Tickets">
      <div style={styles.container}>
        {error && <p style={styles.errorText}>Error: {error}</p>}

        {/* Create Ticket Form */}
        <form style={styles.createFormSection} onSubmit={handleCreateTicket}>
          <div style={styles.formTitle}>Create New Ticket</div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Subject</label>
            <input
              style={styles.input}
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Brief description of your issue"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              style={styles.textarea}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Provide detailed information about your issue"
            />
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Category</label>
              <select
                style={styles.select}
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="general">General Inquiry</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing Question</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Priority</label>
              <select
                style={styles.select}
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          <button
            style={styles.submitButton}
            type="submit"
            disabled={creating}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#3B82F6';
            }}
          >
            {creating ? 'Creating...' : 'Create Ticket'}
          </button>
        </form>

        {/* Tickets List */}
        <div style={styles.ticketsList}>
          {tickets && tickets.length > 0 ? (
            tickets.map((ticket) => (
              <div
                key={ticket.id}
                style={{
                  ...styles.ticketItem,
                  ...(expandedId === ticket.id ? styles.ticketItemExpanded : {}),
                }}
                onClick={() => handleExpandTicket(ticket.id)}
                onMouseEnter={(e) => {
                  if (expandedId !== ticket.id) {
                    e.currentTarget.style.borderColor = '#3B82F6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (expandedId !== ticket.id) {
                    e.currentTarget.style.borderColor = '#1F2937';
                  }
                }}
              >
                <div style={styles.ticketHeader}>
                  <div style={styles.ticketNumber}>#{ticket.id}</div>
                  <div style={styles.ticketTitle}>{ticket.subject}</div>
                  <div>{getStatusBadge(ticket.status)}</div>
                  <div>{getPriorityBadge(ticket.priority)}</div>
                </div>

                {expandedId === ticket.id && (
                  <div style={styles.ticketDetails}>
                    <div style={styles.ticketDetail}>
                      <strong>Created:</strong> {ticket.createdAt}
                    </div>
                    <div style={styles.ticketDetail}>
                      <strong>Category:</strong> {ticket.category}
                    </div>
                    <div style={styles.ticketDetail}>
                      <strong>Description:</strong> {ticket.description}
                    </div>

                    {ticketMessages[ticket.id] && (
                      <div style={styles.messageThread}>
                        {ticketMessages[ticket.id].map((msg, idx) => (
                          <div key={idx} style={styles.message}>
                            <div style={styles.messageSender}>{msg.sender}</div>
                            <div style={styles.messageText}>{msg.text}</div>
                            <div style={styles.messageTime}>{msg.timestamp}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p style={styles.loadingText}>No tickets found</p>
          )}
        </div>
      </div>
    </ClientShell>
  );
}
